import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// AHP patriotic palette — single source of truth in HospitalProfile.tsx Quality cards.
const PALETTE = {
  navy: 'var(--civic-navy-deeper)',
  navyMid: 'var(--civic-navy)',
  red: 'var(--civic-red)',
  gold: 'var(--civic-gold)',
  cream: 'var(--civic-cream)',
  parchment: '#FBF7EE',
  emerald: '#10B981',
  sky: '#5B9BD5',
};

interface PlatformStatsRow {
  hospitals_count: number | null;
  countries_count: number | null;
  languages_count: number | null;
  scenarios_count: number | null;
  crosswalk_count: number | null;
  translations_count: number | null;
  chargemaster_mappings_count: number | null;
  // Narration funnel — `written` is the larger pool (every visual-novel line
  // ever authored), `voice_lines_count` is the subset where ElevenLabs has
  // actually rendered an MP3. Showing both side-by-side reads as a funnel
  // ("5.4K written, of which 758 are voice-acted") rather than a single
  // ambiguous "voice lines" number.
  voice_lines_written_count: number | null;
  voice_lines_count: number | null;
  scenarios_voice_acted_count: number | null;
  refreshed_at: string;
}

// Number-formatting tuned for stat-bar use — shows scale, not exact digits.
// 843,012,847 → "843M", 442,154 → "442K", 12,252 → "12.3K", 33 → "33"
function abbreviate(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
  if (n >= 100_000) return `${Math.round(n / 1_000)}K`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

interface StatItem {
  label: string;
  value: number | null;
  href: string | null;            // null = not clickable
  accent: string;
  suffix?: string;                // e.g., "+", "languages"
}

// AHP-styled "stat chip" — patriotic underline accent on hover when clickable.
function StatChip({ item, t }: { item: StatItem; t: any }) {
  const formatted = abbreviate(item.value);
  const inner = (
    <span
      className="inline-flex items-baseline gap-1.5 group"
      style={{ color: t.bodyText }}
    >
      <span
        className="font-semibold tabular-nums leading-none"
        style={{
          fontFamily: "'Instrument Serif', Georgia, serif",
          fontWeight: 400,
          fontSize: '1.45rem',
          color: t.headingText,
          letterSpacing: '-0.02em',
        }}
      >
        {formatted}{item.suffix || ''}
      </span>
      <span
        className="text-xs uppercase tracking-wider"
        style={{ color: t.fadedText, fontWeight: 500 }}
      >
        {item.label}
      </span>
    </span>
  );
  if (!item.href) return inner;
  return (
    <Link
      to={item.href}
      className="hover:no-underline group transition-colors rounded px-1 -mx-1"
      style={{ textDecoration: 'none' }}
      title={`See ${item.label.toLowerCase()} detail →`}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${item.accent}15`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {inner}
    </Link>
  );
}

interface PlatformStatBarProps {
  isDark: boolean;
}

export const PlatformStatBar: React.FC<PlatformStatBarProps> = ({ isDark }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['platform-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_stats')
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      return data as PlatformStatsRow | null;
    },
    staleTime: 5 * 60 * 1000, // 5 min — stats refresh nightly anyway
  });

  // Theme tokens mirror the rest of /release-notes (Quality-card system).
  const t = isDark ? {
    bandBg: 'linear-gradient(180deg, rgba(2,22,60,0.92) 0%, rgba(0,14,44,0.96) 100%)',
    bandBorder: 'rgba(var(--civic-gold-rgb),0.18)',
    bandShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 20px -14px rgba(0,0,0,0.6)',
    headingText: PALETTE.cream,
    bodyText: 'rgba(var(--civic-parchment-rgb),0.78)',
    fadedText: 'rgba(var(--civic-parchment-rgb),0.50)',
    divider: 'rgba(var(--civic-gold-rgb),0.18)',
  } : {
    bandBg: 'linear-gradient(180deg, #FFFFFF 0%, #FBF7EE 100%)',
    bandBorder: 'rgba(var(--civic-navy-deeper-rgb),0.12)',
    bandShadow: '0 1px 0 rgba(255,255,255,0.6) inset, 0 4px 14px -6px rgba(var(--civic-navy-deeper-rgb),0.10)',
    headingText: PALETTE.navy,
    bodyText: 'rgba(var(--civic-navy-deeper-rgb),0.78)',
    fadedText: 'rgba(var(--civic-navy-deeper-rgb),0.50)',
    divider: 'rgba(var(--civic-gold-rgb),0.30)',
  };

  // Stat items, in display order. Each gets a deep-link if a detail page exists.
  // "+" suffix on hospital count signals "approximate / always growing".
  const items: StatItem[] = [
    { label: 'hospitals',                    value: data?.hospitals_count ?? null,             href: '/hospital-rankings',           accent: PALETTE.red,     suffix: '+' },
    { label: 'countries',                    value: data?.countries_count ?? null,             href: '/international',               accent: PALETTE.sky },
    { label: 'languages',                    value: data?.languages_count ?? null,             href: null,                           accent: PALETTE.gold },
    { label: 'scenarios created',            value: data?.scenarios_count ?? null,             href: '/scenarios',                   accent: PALETTE.emerald },
    { label: 'intl codes cross-mapped',      value: data?.crosswalk_count ?? null,             href: '/international/code-explorer', accent: PALETTE.sky },
    { label: 'translations',                 value: data?.translations_count ?? null,          href: null,                           accent: PALETTE.gold,    suffix: '+' },
    // Narration pair — written first (the larger pool), then audio-rendered
    // subset. Reads as a funnel left-to-right.
    { label: 'narration lines written',      value: data?.voice_lines_written_count ?? null,   href: null,                           accent: PALETTE.gold },
    { label: 'voice-acted (ElevenLabs)',     value: data?.voice_lines_count ?? null,           href: null,                           accent: PALETTE.red },
    { label: 'pricing rows ingested',        value: data?.chargemaster_mappings_count ?? null, href: '/ingestion-dashboard',         accent: PALETTE.navy,    suffix: '+' },
  ];

  if (isLoading) {
    return (
      <div
        className="rounded-lg p-4 mb-6 text-sm text-center"
        style={{ background: t.bandBg, border: `1px solid ${t.bandBorder}`, color: t.fadedText }}
      >
        Loading platform stats…
      </div>
    );
  }

  return (
    <div
      className="rounded-lg px-4 sm:px-5 py-3.5 mb-6 relative overflow-hidden"
      style={{ background: t.bandBg, border: `1px solid ${t.bandBorder}`, boxShadow: t.bandShadow }}
    >
      {/* Patriotic flag-stripe top accent — same device as Quality cards */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(90deg, ${PALETTE.red} 0%, ${PALETTE.cream} 50%, ${PALETTE.navyMid} 100%)` }}
        aria-hidden
      />
      <div className="text-[10px] uppercase tracking-widest mb-2.5" style={{ color: t.fadedText }}>
        Platform scale · live
      </div>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2.5 sm:gap-x-6">
        {items.map((it, i) => (
          <React.Fragment key={it.label}>
            <StatChip item={it} t={t} />
            {i < items.length - 1 && (
              <span aria-hidden className="hidden sm:inline" style={{ color: t.divider, fontSize: '0.6rem' }}>•</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
